/**
 * Cognito Configuration Utilities
 * 
 * This module provides utilities for managing Cognito User Pool configuration,
 * custom attributes, and user roles.
 */

// User role types
export type UserRole = 'creator' | 'admin';

// Subscription tier types (for future pricing implementation)
export type SubscriptionTier = 'free' | 'pro' | 'enterprise';

// Custom attribute keys used in Cognito User Pool
export const COGNITO_CUSTOM_ATTRIBUTES = {
  ROLE: 'custom:role',
  SUBSCRIPTION_TIER: 'custom:subscriptionTier',
  ONBOARDING_COMPLETED: 'custom:onboardingCompleted',
} as const;

// Default values for new users
export const DEFAULT_USER_ATTRIBUTES = {
  role: 'creator' as UserRole,
  subscriptionTier: 'free' as SubscriptionTier,
  onboardingCompleted: 'false',
};

// Password policy configuration
export const PASSWORD_POLICY = {
  minLength: 8,
  requireLowercase: true,
  requireUppercase: true,
  requireNumbers: true,
  requireSpecialCharacters: true,
} as const;

// Validation functions
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  // Basic regex check
  if (!emailRegex.test(email)) {
    return false;
  }
  
  // Additional checks for edge cases
  if (email.includes('..')) {
    return false; // No consecutive dots
  }
  
  if (email.startsWith('.') || email.endsWith('.')) {
    return false; // No leading or trailing dots
  }
  
  return true;
};

export const validatePassword = (password: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (password.length < PASSWORD_POLICY.minLength) {
    errors.push(`Must be at least ${PASSWORD_POLICY.minLength} characters long`);
  }

  if (PASSWORD_POLICY.requireLowercase && !/[a-z]/.test(password)) {
    errors.push('Must contain at least one lowercase letter');
  }

  if (PASSWORD_POLICY.requireUppercase && !/[A-Z]/.test(password)) {
    errors.push('Must contain at least one uppercase letter');
  }

  if (PASSWORD_POLICY.requireNumbers && !/\d/.test(password)) {
    errors.push('Must contain at least one number');
  }

  if (PASSWORD_POLICY.requireSpecialCharacters && !/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    errors.push('Must contain at least one special character');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const validateName = (name: string): boolean => {
  const trimmed = name.trim();
  
  // Check length requirements
  if (trimmed.length < 2 || trimmed.length > 50) {
    return false;
  }
  
  // Check that the name contains at least one letter
  if (!/[a-zA-Z]/.test(trimmed)) {
    return false;
  }
  
  // Check that the name is not only whitespace and special characters
  if (!/[a-zA-Z0-9]/.test(trimmed)) {
    return false;
  }
  
  // Check that the trimmed name is mostly letters and reasonable characters
  // Allow letters, numbers, spaces, hyphens, apostrophes, and periods
  if (!/^[a-zA-Z0-9\s\-'.]+$/.test(trimmed)) {
    return false;
  }
  
  // Check that it's not just special characters with minimal letters
  // At least 50% of non-space characters should be letters or numbers
  const nonSpaceChars = trimmed.replace(/\s/g, '');
  const letterNumberCount = (nonSpaceChars.match(/[a-zA-Z0-9]/g) || []).length;
  const ratio = letterNumberCount / nonSpaceChars.length;
  
  if (ratio < 0.5) {
    return false;
  }
  
  return true;
};

// Helper functions for custom attributes
export const formatCustomAttributes = (attributes: {
  role?: UserRole;
  subscriptionTier?: SubscriptionTier;
  onboardingCompleted?: boolean;
}) => {
  const customAttributes: Record<string, string> = {};

  if (attributes.role) {
    customAttributes[COGNITO_CUSTOM_ATTRIBUTES.ROLE] = attributes.role;
  }

  if (attributes.subscriptionTier) {
    customAttributes[COGNITO_CUSTOM_ATTRIBUTES.SUBSCRIPTION_TIER] = attributes.subscriptionTier;
  }

  if (attributes.onboardingCompleted !== undefined) {
    customAttributes[COGNITO_CUSTOM_ATTRIBUTES.ONBOARDING_COMPLETED] = 
      attributes.onboardingCompleted.toString();
  }

  return customAttributes;
};

export const parseCustomAttributes = (attributes: Record<string, string>) => {
  return {
    role: (attributes[COGNITO_CUSTOM_ATTRIBUTES.ROLE] as UserRole) || DEFAULT_USER_ATTRIBUTES.role,
    subscriptionTier: (attributes[COGNITO_CUSTOM_ATTRIBUTES.SUBSCRIPTION_TIER] as SubscriptionTier) || 
                     DEFAULT_USER_ATTRIBUTES.subscriptionTier,
    onboardingCompleted: attributes[COGNITO_CUSTOM_ATTRIBUTES.ONBOARDING_COMPLETED] === 'true',
  };
};

// Error message mappings for common Cognito errors
export const COGNITO_ERROR_MESSAGES: Record<string, string> = {
  'UserNotFoundException': 'No account found with this email address.',
  'NotAuthorizedException': 'Incorrect email or password.',
  'UserNotConfirmedException': 'Please verify your email address before signing in.',
  'PasswordResetRequiredException': 'Password reset is required. Please check your email.',
  // UserLambdaValidationException is intentionally NOT mapped here
  // We want to preserve the exact error message from the Lambda function
  'InvalidPasswordException': 'Password does not meet requirements.',
  'InvalidParameterException': 'Invalid request parameters.',
  'CodeMismatchException': 'Invalid verification code.',
  'ExpiredCodeException': 'Verification code has expired.',
  'LimitExceededException': 'Too many attempts. Please try again later.',
  'TooManyRequestsException': 'Too many requests. Please try again later.',
  'UsernameExistsException': 'An account with this email already exists.',
  'InvalidUserPoolConfigurationException': 'User pool configuration error.',
  'NetworkError': 'Network error. Please check your connection and try again.',
};

export const getErrorMessage = (error: any): string => {
  if (typeof error === 'string') {
    if (error.trim() === '') {
      return 'An unexpected error occurred. Please try again.';
    }
    
    const mappedMessage = COGNITO_ERROR_MESSAGES[error];
    if (mappedMessage) {
      return mappedMessage;
    }
    
    // For unknown error codes, ensure proper formatting
    // If the error doesn't start with a letter, provide a generic message
    if (!/^[a-zA-Z]/.test(error)) {
      return 'An unexpected error occurred. Please try again.';
    }
    
    // Check if the error is a property name that would return a function or object
    // This prevents issues with property names like 'valueOf', 'toString', '__proto__', etc.
    try {
      const testObj = {};
      const propertyValue = (testObj as any)[error];
      if (typeof propertyValue === 'function' || typeof propertyValue === 'object') {
        return 'An unexpected error occurred. Please try again.';
      }
    } catch {
      // If accessing the property throws an error, continue with normal processing
    }
    
    const formattedError = error.charAt(0).toUpperCase() + error.slice(1);
    if (!formattedError.match(/[.!]$/)) {
      return formattedError + '.';
    }
    return formattedError;
  }

  // For UserLambdaValidationException, preserve the exact error message from Lambda
  // This is critical for invite code validation errors
  if (error?.name === 'UserLambdaValidationException' && error?.message) {
    // Strip out technical prefixes like "PreSignUp failed with error"
    let message = error.message;
    message = message.replace(/^PreSignUp failed with error\s*/i, '');
    message = message.replace(/^PostConfirmation failed with error\s*/i, '');
    message = message.replace(/^PreAuthentication failed with error\s*/i, '');
    return message;
  }

  if (error?.name && COGNITO_ERROR_MESSAGES[error.name]) {
    return COGNITO_ERROR_MESSAGES[error.name];
  }

  if (error?.message) {
    let message = error.message;
    
    // Strip out technical prefixes from any error message
    message = message.replace(/^PreSignUp failed with error\s*/i, '');
    message = message.replace(/^PostConfirmation failed with error\s*/i, '');
    message = message.replace(/^PreAuthentication failed with error\s*/i, '');
    
    // If the message doesn't start with a letter, provide a generic message
    if (!/^[a-zA-Z]/.test(message)) {
      return 'An unexpected error occurred. Please try again.';
    }
    
    // Return message as-is if it already has proper capitalization and punctuation
    // This preserves exact error messages from Lambda functions
    if (message[0] === message[0].toUpperCase()) {
      return message;
    }
    
    const formattedMessage = message.charAt(0).toUpperCase() + message.slice(1);
    if (!formattedMessage.match(/[.!]$/)) {
      return formattedMessage + '.';
    }
    return formattedMessage;
  }

  return 'An unexpected error occurred. Please try again.';
};